import type { ThreadSortBy, ReportReason, ReportStatus } from '@/types/residents/corkBoard';

// ============================================================================
// API Endpoints
// ============================================================================

export const CORK_BOARD_API_BASE = '/api/v1/cork-board';

export const CORK_BOARD_API = {
  tags: {
    base: `${CORK_BOARD_API_BASE}/tags`,
    byId: (id: number) => `${CORK_BOARD_API_BASE}/tags/${id}`,
    reorder: `${CORK_BOARD_API_BASE}/tags/reorder`,
  },
  threads: {
    base: `${CORK_BOARD_API_BASE}/threads`,
    byId: (id: string) => `${CORK_BOARD_API_BASE}/threads/${id}`,
    pin: (id: string) => `${CORK_BOARD_API_BASE}/threads/${id}/pin`,
    lock: (id: string) => `${CORK_BOARD_API_BASE}/threads/${id}/lock`,
    save: (id: string) => `${CORK_BOARD_API_BASE}/threads/${id}/save`,
    view: (id: string) => `${CORK_BOARD_API_BASE}/threads/${id}/view`,
    replies: (threadId: string) => `${CORK_BOARD_API_BASE}/threads/${threadId}/replies`,
  },
  replies: {
    byId: (id: string) => `${CORK_BOARD_API_BASE}/replies/${id}`,
  },
  myThreads: `${CORK_BOARD_API_BASE}/my-threads`,
  saved: `${CORK_BOARD_API_BASE}/saved`,
  announcements: {
    base: `${CORK_BOARD_API_BASE}/announcements`,
  },
  reports: {
    base: `${CORK_BOARD_API_BASE}/reports`,
    byId: (id: string) => `${CORK_BOARD_API_BASE}/reports/${id}`,
  },
  notifications: {
    base: `${CORK_BOARD_API_BASE}/notifications`,
    markAsRead: (id: string) => `${CORK_BOARD_API_BASE}/notifications/${id}/read`,
    delete: (id: string) => `${CORK_BOARD_API_BASE}/notifications/${id}`,
  },
} as const;

// ============================================================================
// Cache Tags (for RTK Query)
// ============================================================================

export const CORK_BOARD_CACHE_TAGS = {
  Tags: 'CorkBoardTags',
  Threads: 'CorkBoardThreads',
  Thread: 'CorkBoardThread',
  MyThreads: 'CorkBoardMyThreads',
  SavedThreads: 'CorkBoardSavedThreads',
  Reports: 'CorkBoardReports',
  Notifications: 'CorkBoardNotifications',
} as const;

// ============================================================================
// Input Limits
// ============================================================================

export const CORK_BOARD_INPUT_LIMITS = {
  titleMax: 255,
  bodyMax: 10000,
  minTags: 1,
  maxTags: 1,
  reportDetailsMax: 2000,
  searchMax: 255,
  tagNameMax: 100,
} as const;

// ============================================================================
// Tag Management Presets
// ============================================================================

export const TAG_PRESET_COLORS = [
  '#6B7280',
  '#3B82F6',
  '#8B5CF6',
  '#F59E0B',
  '#EC4899',
  '#10B981',
  '#EF4444',
  '#F97316',
  '#1D4ED8',
  '#14B8A6',
  '#A855F7',
  '#84CC16',
] as const;

export const TAG_PRESET_ICONS = [
  '💬', '🛠️', '💼', '🏷️', '📅', '🤝',
  '🔍', '⭐', '📢', '📚', '🎉', '🏠',
  '🚗', '🐾', '🎵', '🍕', '💡', '❓',
] as const;

// ============================================================================
// Pagination
// ============================================================================

export const CORK_BOARD_PAGINATION = {
  defaultPage: 0,
  defaultPageSize: 20,
  maxPageSize: 50,
} as const;

// ============================================================================
// Sort Options
// ============================================================================

export const CORK_BOARD_SORT_OPTIONS: { value: ThreadSortBy; label: string }[] = [
  { value: 'lastActivity', label: 'Latest Activity' },
  { value: 'newest', label: 'Newest First' },
  { value: 'mostReplies', label: 'Most Replied' },
  { value: 'mostViewed', label: 'Most Viewed' },
];

// ============================================================================
// Report Reasons
// ============================================================================

export const CORK_BOARD_REPORT_REASONS: { value: ReportReason; label: string }[] = [
  { value: 'spam', label: 'Spam' },
  { value: 'inappropriate', label: 'Inappropriate' },
  { value: 'harassment', label: 'Harassment' },
  { value: 'other', label: 'Other' },
];

// ============================================================================
// Report Status Options
// ============================================================================

export const CORK_BOARD_REPORT_STATUSES: { value: ReportStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'dismissed', label: 'Dismissed' },
];

// ============================================================================
// Report Badge Styles
// ============================================================================

export const REPORT_STATUS_STYLES: Record<string, { bg: string; text: string }> = {
  pending: { bg: '#FEF3C7', text: '#92400E' },
  reviewed: { bg: '#DBEAFE', text: '#1E40AF' },
  resolved: { bg: '#D1FAE5', text: '#065F46' },
  dismissed: { bg: '#F3F4F6', text: '#6B7280' },
};

export const REPORT_REASON_STYLES: Record<string, { bg: string; text: string }> = {
  spam: { bg: '#FEE2E2', text: '#991B1B' },
  inappropriate: { bg: '#FEF3C7', text: '#92400E' },
  harassment: { bg: '#FCE7F3', text: '#9D174D' },
  other: { bg: '#E5E7EB', text: '#374151' },
};

// ============================================================================
// File Upload
// ============================================================================

export const CORK_BOARD_FILE_UPLOAD = {
  maxFiles: 5,
  maxSizeMB: 10,
  maxSizeBytes: 10 * 1024 * 1024,
  allowedTypes: [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
    'image/heic',
    'image/heif',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
} as const;
